import { message } from "antd";
import { Component } from "react";
import { type RouteComponentProps } from "react-router";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { type CostType } from "types/enum";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { setLoadingAction } from "utilities/form/form";
import { routes } from "utilities/routes";

import { BTModal, type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { RouteRelative } from "commonComponents/utilities/RouteRelative/RouteRelative";

import { costCodeDelete } from "entity/costCode/common/CostCode.utility";
import { CostCodeConflictModal } from "entity/costCode/CostCodeDetails/CostCodeConflict";

import {
    type CostCodeConflict,
    type CostCodeDetailsFormActions,
    type CostCodeEntity,
    type CostCodeUpdateResponse,
    type ICostCodeDetailsFormValues,
} from "./CostCode.api.types";
import {
    CostCodeDetailsHandler,
    type ICostCodeDetailsHandler,
} from "./CostCodeDetailsHandler.api.handler";
import { CostCodeDetailsPresentational } from "./CostCodeDetailsPresentational";

interface IUrlProps {}

export interface ICostCodeDetailsProps extends RouteComponentProps<IUrlProps> {
    costCodeId: number;
    costType: CostType;
    shiftId?: number;
    fromCostCodeSelector?: boolean;
    disableDelete?: boolean;
    jobId?: number;
    laborCodesOnly?: boolean;
    /**
     * Cost code details handler
     * @default new CostCodeDetailsHandler()
     */
    handler?: ICostCodeDetailsHandler;
    /**
     * Use modal flag
     * @default true
     */
    useModal?: boolean;
    modalConfig?: IModalConfiguration;
    /**
     * index is needed when rendering CostCodeDetails in a list
     */
    index?: number;
    /**
     * On add handler
     * @default (): any => {}
     */
    onAdd?: (newCode: BTSelectItem<any>) => void;
    /**
     * On edit handler
     * @default (): any => {}
     */
    onEdit?: (updatedCode: BTSelectItem<any>) => void;
    /**
     * On delete handler
     * @default (): any => {}
     */
    onDelete?: (deletedCode: BTSelectItem<any> | null) => void;
    onSaveAndNew?: () => void;
    /**
     * On before close handler
     * @default (): any => {}
     */
    beforeClose?: () => void;
}

interface ICostCodeDetailsState {
    data?: CostCodeEntity;
    actionBeingPerformed: CostCodeDetailsFormActions;
    conflictList?: CostCodeConflict[];
}

class CostCodeDetailsInternal extends Component<ICostCodeDetailsProps, ICostCodeDetailsState> {
    static defaultProps = {
        handler: new CostCodeDetailsHandler(),
        useModal: true,
        onEdit: (): any => {},
        onAdd: (): any => {},
        onDelete: (): any => {},
        beforeClose: (): any => {},
    };

    state: Readonly<ICostCodeDetailsState> = {
        data: undefined,
        actionBeingPerformed: undefined,
    };

    private getParentRoute = () => {
        return this.props.modalConfig?.parentRoute ?? "";
    };

    componentDidMount = async () => {
        const { costCodeId, costType, laborCodesOnly } = this.props;

        try {
            await this.getCostCodeData(costType, costCodeId, laborCodesOnly || false);
        } catch (e) {
            this.setState(() => {
                throw e;
            });
        }
    };

    private getCostCodeData = async (
        costType: CostType,
        costCodeId: number,
        laborCodesOnly: boolean,
        categoryId?: number,
    ) => {
        const apiResponse = await this.props.handler!.get(
            costType,
            costCodeId,
            laborCodesOnly,
            categoryId,
        );
        if (categoryId) {
            apiResponse.categoryId = categoryId;
        }
        this.setState({
            data: apiResponse,
            actionBeingPerformed: undefined,
        });
    };

    private handleSave = async (
        formData: ICostCodeDetailsFormValues,
        action: CostCodeDetailsFormActions,
    ) => {
        const { data } = this.state;
        const { costType, fromCostCodeSelector } = this.props;
        const costCodeId = data!.costCodeId;

        // data should be defined here as onSubmit can only be called from child component which isnt rendering until data is truthy
        let response: CostCodeUpdateResponse;
        if (costCodeId > 0) {
            response = await this.props.handler!.update(formData, costCodeId);
            this.props.onEdit!(response.selectItem);
        } else {
            response = await this.props.handler!.create(formData);
            this.props.onAdd!(response.selectItem);
        }

        if (action === "saveAndNew" && this.props.onSaveAndNew) {
            this.props.onSaveAndNew();
        }

        const costCodeIdToFetch = action === "saveAndNew" ? 0 : parseInt(response.selectItem.id);
        const newPath = fromCostCodeSelector
            ? routes.costCode.getCostCodeDetailsIndexLink(
                  costCodeIdToFetch,
                  costType,
                  this.props.index,
              )
            : routes.costCode.getCostCodeDetailsLink(costCodeIdToFetch, costType);
        this.props.history.replace(this.getParentRoute() + newPath);

        await this.getCostCodeData(costType, costCodeIdToFetch, this.props.laborCodesOnly ?? false);
        void message.success("Saved successfully");
    };

    private handleSubmit = async (
        formData: ICostCodeDetailsFormValues,
        action: CostCodeDetailsFormActions,
    ) => {
        let success = false;

        await setLoadingAction<CostCodeDetailsFormActions>(this, {
            callbackNavigates: false,
            actionBeingPerformed: action,
            callback: async () => {
                try {
                    await this.handleSave(formData, action);
                    success = true;
                } catch (e) {
                    showAPIErrorMessage(e);
                    this.setState({ actionBeingPerformed: undefined });
                    return;
                }
            },
        });
        return success;
    };

    private handleDelete = async () => {
        const { costType } = this.props;
        const { data } = this.state;
        const costCodeId = data!.costCodeId;

        if (costCodeId <= 0) {
            // Can not delete a new Cost/Variance Code not yet submitted
            return;
        }

        await setLoadingAction<CostCodeDetailsFormActions>(this, {
            actionBeingPerformed: "delete",
            callbackNavigates: false,
            callback: async () => {
                await costCodeDelete(
                    costCodeId,
                    costType,
                    this.props.handler!.delete,
                    this.props.beforeClose!,
                    this.handleOpenConflictModal,
                    () => this.setState({ actionBeingPerformed: undefined }),
                    this.props.onDelete,
                );
            },
        });
    };

    private closeConflictModal = () => {
        const search = this.props.history.location.search;
        this.props.history.push(this.props.match.url + (search ? search : ""));
    };

    private handleOpenConflictModal = async () => {
        const data = await this.props.handler!.conflict(this.state.data!.costCodeId);
        this.setState({ conflictList: data.conflictList, actionBeingPerformed: undefined });

        this.props.history.push(
            `${this.props.history.location.pathname}${routes.costCode.getCostCodeConflictLink()}`,
        );
    };

    render() {
        if (!this.state || !this.state.data) {
            return <BTLoading />;
        }

        const { data, actionBeingPerformed, conflictList } = this.state;

        const conflictModal = (
            <RouteRelative
                path={routes.costCode.costCodeConflict}
                render={() => {
                    return (
                        <CostCodeConflictModal
                            onClose={this.closeConflictModal}
                            conflictList={conflictList}
                        />
                    );
                }}
            />
        );
        const modalTitle = `${this.state.data.friendlyName} code`;

        const costCodeDetails = (
            <>
                <CostCodeDetailsPresentational
                    entity={data}
                    laborCodesOnly={this.props.laborCodesOnly}
                    actionBeingPerformed={actionBeingPerformed}
                    onSubmit={this.handleSubmit}
                    onDelete={this.handleDelete}
                    disableDelete={this.props.disableDelete}
                    title={modalTitle}
                    modalConfig={
                        this.props.beforeClose
                            ? { beforeClose: this.props.beforeClose, parentRoute: "" }
                            : undefined
                    }
                />
                {conflictList && conflictList.length > 0 && conflictModal}
            </>
        );

        if (this.props.useModal!) {
            return (
                <BTModal
                    data-testid="btModalCostCodeDetails"
                    beforeClose={() => this.props.beforeClose!()}
                    title={modalTitle}
                    width="650px"
                    removeBodyPadding={true}
                    visible={true}
                    useModalLayout
                >
                    {costCodeDetails}
                </BTModal>
            );
        } else {
            return costCodeDetails;
        }
    }
}

export const CostCodeDetails = withErrorBoundary(CostCodeDetailsInternal)(
    "Could not load Cost Code Details",
);
export default CostCodeDetails;
